import React, { useMemo, useState } from "react";
import { Plus, Trash2, Fence, Building2, Footprints, DoorOpen, Pencil, MousePointer2, Undo2, RotateCcw } from "lucide-react";
import { PlannerCanvas } from "../planner/PlannerCanvas";
import { SegmentTable } from "../planner/SegmentTable";
import { AnsichtCanvas } from "../planner/AnsichtCanvas";
import { GATE_DEFAULTS, setSegmentLength, setVertexAngle, flipVertex, removeSegment } from "../../lib/planner";
import { PROJECT_TYPES, FORMS, FORM_SIDES, newProject, newGate, hasMotor, projectPlan, projectLayout, sockelSides, shapePoints, gateAnsicht } from "../../lib/publicPlan";

const inp = "w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black";
const Label = ({ children }) => <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{children}</label>;
const ICONS = { fence: Fence, balcony: Building2, stairs: Footprints, gate: DoorOpen };
export const UNIT_MM = { mm: 1, cm: 10, m: 1000 };

// Clearable number input (fixes mobile "0 bleibt stehen"); optional unit factor (mm intern)
export function NumInput({ value, onChange, unit = 1, className = inp, testId, step, min, placeholder }) {
  const [draft, setDraft] = useState(null);
  const fmt = (v) => (v == null || Number.isNaN(v) ? "" : String(Math.round((v / unit) * 1000) / 1000));
  const disp = draft !== null ? draft : fmt(value);
  return (
    <input type="number" inputMode="decimal" step={step} min={min} placeholder={placeholder} className={className} data-testid={testId}
      value={disp}
      onFocus={() => setDraft(fmt(value))}
      onChange={(e) => { const v = e.target.value; setDraft(v); if (v !== "" && !Number.isNaN(Number(v))) onChange(Math.round(Number(v) * unit)); }}
      onBlur={() => { if (draft === "") onChange(0); setDraft(null); }} />
  );
}

function Choice({ options, value, onChange, testId, cols = "grid-cols-2 sm:grid-cols-4" }) {
  return (
    <div className={`grid ${cols} gap-2`} data-testid={testId}>
      {options.map(([k, label, hint]) => (
        <button type="button" key={k} onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
                className={`text-left border px-3 py-2.5 transition-colors ${value === k ? "border-zinc-900 ring-1 ring-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-500 bg-white"}`}>
          <div className="text-sm font-semibold text-zinc-900">{label}</div>
          {hint && <div className="text-[11px] text-zinc-500 leading-tight mt-0.5">{hint}</div>}
        </button>
      ))}
    </div>
  );
}

function ShapePreview({ p }) {
  const { points, closed } = shapePoints(p);
  if (points.length < 2) return null;
  const xs = points.map((q) => q.x), ys = points.map((q) => q.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const W = Math.max(maxX - minX, 1000), H = Math.max(maxY - minY, 600), M = Math.max(W, H) * 0.14;
  const fs = Math.max(W, H) / 22;
  const sides = FORM_SIDES[p.form] || [];
  const d = points.map((q) => `${q.x - minX + M},${q.y - minY + M}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W + 2 * M} ${H + 2 * M}`} className="w-full max-h-40 bg-zinc-50 border border-zinc-200" data-testid="shape-preview">
      {closed ? <polygon points={d} fill="none" stroke="#18181b" strokeWidth={fs / 4} /> : <polyline points={d} fill="none" stroke="#18181b" strokeWidth={fs / 4} />}
      {points.map((q, i) => <circle key={i} cx={q.x - minX + M} cy={q.y - minY + M} r={fs / 3} fill="#18181b" />)}
      {points.slice(0, closed ? points.length : points.length - 1).map((q, i) => {
        const r = points[(i + 1) % points.length]; const mx = (q.x + r.x) / 2 - minX + M, my = (q.y + r.y) / 2 - minY + M;
        const vert = Math.abs(r.x - q.x) < Math.abs(r.y - q.y);
        const label = sides[i] || String.fromCharCode(65 + i);
        return <text key={i} x={mx + (vert ? fs * 0.6 : 0)} y={my + (vert ? 0 : -fs * 0.5)} fontSize={fs} textAnchor={vert ? "start" : "middle"} fill="#2563eb" fontWeight="600">{`${label} ${Math.round(Math.hypot(r.x - q.x, r.y - q.y))}`}</text>;
      })}
    </svg>
  );
}

function FreeForm({ p, up, base }) {
  const [mode, setMode] = useState("draw");
  const [sel, setSel] = useState(null);
  const plan = projectPlan(p, base);
  const layout = projectLayout(p, base);
  const setPlan = (fn) => { const np = fn(plan); up({ points: np.points, closed: np.closed, gates: np.gates }); };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {[["draw", Pencil, "Zeichnen"], ["edit", MousePointer2, "Verschieben"]].map(([m, I, l]) => (
          <button type="button" key={m} onClick={() => setMode(m)} className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border rounded-sm ${mode === m ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 bg-white"}`} data-testid={`free-mode-${m}`}><I size={12}/> {l}</button>
        ))}
        <div className="flex-1"/>
        <button type="button" onClick={() => up({ points: p.points.slice(0, -1), closed: false })} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-zinc-300 rounded-sm bg-white" data-testid="free-undo"><Undo2 size={12}/> Rückgängig</button>
        <button type="button" onClick={() => up({ points: [], closed: false, gates: [] })} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-zinc-300 rounded-sm bg-white" data-testid="free-reset"><RotateCcw size={12}/> Neu</button>
      </div>
      <PlannerCanvas plan={plan} layout={layout} mode={mode} ortho
        onAddPoint={(pt) => { if (!p.closed) up({ points: [...p.points, pt] }); }}
        onMovePoint={(i, pt) => up({ points: p.points.map((q, j) => (j === i ? pt : q)) })}
        onClose={() => { up({ closed: true }); setMode("edit"); }} selectedSeg={sel} onSelectSeg={setSel} />
      <SegmentTable layout={layout} plan={plan} selectedSeg={sel} onSelectSeg={setSel}
        onLength={(i, len) => setPlan((q) => setSegmentLength(q, i, len))} onAngle={(i, a) => setPlan((q) => setVertexAngle(q, i, a))}
        onFlip={(i) => setPlan((q) => flipVertex(q, i))} onRemoveSegment={(i) => { setPlan((q) => removeSegment(q, i)); setSel(null); }}
        onAddGate={(seg, offset) => up({ gates: [...p.gates, newGate(seg, offset)] })}
        onGate={(id, patch) => up({ gates: p.gates.map((g) => (g.id === id ? { ...g, ...patch } : g)) })}
        onRemoveGate={(id) => up({ gates: p.gates.filter((g) => g.id !== id) })} />
    </div>
  );
}

function ShapeEditor({ p, up, base, withFree }) {
  const forms = Object.entries(FORMS).filter(([k]) => withFree || k !== "free").filter(([k]) => p.type !== "balcony" || k !== "closed");
  const unit = UNIT_MM[base?.unit] || 1, U = base?.unit || "mm";
  return (
    <div className="space-y-3">
      <Choice options={forms.map(([k, l]) => [k, l])} value={p.form} onChange={(form) => up({ form, gates: [] })} testId={`form-${p.id}`} cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" />
      {p.form === "free" ? <FreeForm p={p} up={up} base={base} /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          <div className="grid grid-cols-3 gap-2">
            {FORM_SIDES[p.form].map((k) => (
              <div key={k}><Label>Seite {k} ({U})</Label><NumInput step={100 / unit} min={300 / unit} unit={unit} value={p.dims[k]} onChange={(v) => up({ dims: { ...p.dims, [k]: v } })} testId={`dim-${p.id}-${k}`} /></div>
            ))}
          </div>
          <ShapePreview p={p} />
        </div>
      )}
    </div>
  );
}

function GateOptions({ g, set, testId, showPosts, showPos }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 col-span-full" data-testid={`${testId}-options`}>
      {showPos && <div><Label>Position auf der Seite</Label>
        <Choice options={[["links", "Links"], ["rechts", "Rechts"]]} value={g.pos || "links"} onChange={(pos) => set({ pos })} testId={`${testId}-pos`} cols="grid-cols-2" /></div>}
      {showPosts && <div><Label>Torpfosten</Label>
        <Choice options={[["alu", "Alu-Torpfosten"], ["beton", "Zwischen Betonpfeilern"]]} value={g.posts || "alu"} onChange={(posts) => set({ posts })} testId={`${testId}-posts`} cols="grid-cols-2" /></div>}
      {hasMotor(g.type || g.gateType) && <div><Label>Elektrischer Antrieb gewünscht?</Label>
        <Choice options={[["ja", "Ja"], ["nein", "Nein"]]} value={g.motor ? "ja" : "nein"} onChange={(v) => set({ motor: v === "ja" })} testId={`${testId}-motor`} cols="grid-cols-2" /></div>}
    </div>
  );
}

function GatesEditor({ p, up, base }) {
  const layout = projectLayout(p, base);
  const segs = layout.segments;
  const sides = FORM_SIDES[p.form] || [];
  const unit = UNIT_MM[base?.unit] || 1, U = base?.unit || "mm";
  const pfeiler = p.montage === "sockel" && !!p.sockel?.pfeiler;
  const add = () => up({ gates: [...p.gates, newGate(0, 500)] });
  const set = (id, patch) => up({ gates: p.gates.map((g) => (g.id === id ? { ...g, ...patch } : g)) });
  return (
    <div className="space-y-2" data-testid={`gates-${p.id}`}>
      {p.gates.map((g, i) => (
        <div key={g.id} className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end bg-blue-50/50 border border-blue-100 p-2">
          <div><Label>Tor {i + 1}</Label>
            <select value={g.type} onChange={(e) => set(g.id, { type: e.target.value, width: GATE_DEFAULTS[e.target.value].width })} className={inp} data-testid={`gate-type-${p.id}-${i}`}>
              {Object.entries(GATE_DEFAULTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select></div>
          <div><Label>Lichte Breite ({U})</Label><NumInput step={50 / unit} unit={unit} value={g.width} onChange={(v) => set(g.id, { width: v })} testId={`gate-width-${p.id}-${i}`} /></div>
          <div><Label>Seite</Label>
            <select value={g.seg} onChange={(e) => set(g.id, { seg: Number(e.target.value) })} className={inp} data-testid={`gate-side-${p.id}-${i}`}>
              {segs.map((s) => <option key={s.idx} value={s.idx}>{sides[s.idx] || s.label} ({s.length} mm)</option>)}
            </select></div>
          <button type="button" onClick={() => up({ gates: p.gates.filter((x) => x.id !== g.id) })} className="text-xs text-red-600 inline-flex items-center gap-1 pb-2" data-testid={`gate-remove-${p.id}-${i}`}><Trash2 size={12}/> entfernen</button>
          <GateOptions g={g} set={(patch) => set(g.id, patch)} testId={`gate-${p.id}-${i}`} showPosts={!pfeiler} showPos />
        </div>
      ))}
      <button type="button" onClick={add} disabled={!segs.length} className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 border border-blue-300 text-blue-700 rounded-sm bg-white disabled:opacity-40" data-testid={`gate-add-${p.id}`}><Plus size={12}/> Tor hinzufügen</button>
    </div>
  );
}

function SockelEditor({ p, up, base }) {
  const s = p.sockel;
  const setS = (patch) => up({ sockel: { ...s, ...patch } });
  const sides = useMemo(() => sockelSides(p, base), [p, base]);
  const unit = UNIT_MM[base?.unit] || 1, U = base?.unit || "mm";
  const onSockel = p.montage === "sockel";
  return (
    <div className="space-y-3" data-testid={`sockel-${p.id}`}>
      {onSockel && <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div><Label>Sockelhöhe ({U})</Label><NumInput step={50 / unit} unit={unit} value={s.height} onChange={(v) => setS({ height: v })} testId={`sockel-height-${p.id}`} /></div>
        <div className="col-span-2 sm:col-span-3">
          <Label>Betonpfeiler / Mauerpfeiler vorhanden?</Label>
          <Choice options={[["nein", "Nein – Alu-Pfosten auf dem Sockel"], ["ja", "Ja – Zaunfelder zwischen den Pfeilern"]]} value={s.pfeiler ? "ja" : "nein"} onChange={(v) => setS({ pfeiler: v === "ja" })} testId={`pfeiler-${p.id}`} cols="grid-cols-1 sm:grid-cols-2" />
        </div>
        {s.pfeiler && <div><Label>Pfeilerbreite ({U})</Label><NumInput step={50 / unit} unit={unit} value={s.pfeilerWidth} onChange={(v) => setS({ pfeilerWidth: v })} testId={`pfeiler-width-${p.id}`} /></div>}
        {s.pfeiler && <div><Label>Lichte zwischen Pfeilern ca. ({U})</Label><NumInput step={50 / unit} unit={unit} value={s.lichte} onChange={(v) => setS({ lichte: v })} testId={`pfeiler-lichte-${p.id}`} /></div>}
      </div>}
      <p className="text-[11px] text-zinc-500">Vorschau (Ansicht): {onSockel ? `Sockel grau, ${s.pfeiler ? "Pfeiler hellgrau, Zaunfelder dazwischen" : "Alu-Pfosten schwarz auf dem Sockel"}` : "Alu-Pfosten schwarz, Felder automatisch aufgeteilt"}, Tore blau. Pfosten 2 cm höher als Zaun/Tor. Wir gleichen die genauen Maße mit Ihnen ab.</p>
      {sides.map((sd) => (
        <div key={sd.label} data-testid={`sockel-side-${p.id}-${sd.label}`}>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-1">Seite {sd.label} – {sd.length} mm{sd.layout.pfeilerCount ? ` · ${sd.layout.pfeilerCount} Pfeiler` : ""} · {sd.layout.fieldCount} Felder</div>
          <AnsichtCanvas plan={sd.plan} layout={sd.layout} />
        </div>
      ))}
    </div>
  );
}

function StairsPreview({ p, index }) {
  const L = Math.max(Number(p.length) || 1000, 500), n = Math.max(Number(p.steps) || 8, 2), h = Number(p.height) || 1000;
  const rise = Math.min(h / 5, 180), run = L / n; const H = n * rise, M = L * 0.12, fs = L / 28;
  const pts = []; for (let i = 0; i < n; i++) { pts.push(`${M + i * run},${M + H - i * rise}`); pts.push(`${M + (i + 1) * run},${M + H - i * rise}`); }
  const x1 = M, y1 = M + H, x2 = M + (n - 1) * run, y2 = M + H - (n - 1) * rise; // Vorderkante erste → Vorderkante letzte Stufe
  const ang = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
  return (
    <svg viewBox={`0 0 ${L + 2 * M} ${H + 2 * M + fs * 2}`} className="w-full max-h-48 bg-zinc-50 border border-zinc-200" data-testid={`stairs-preview-${index}`}>
      <polyline points={`${M},${M + H + rise} ${pts.join(" ")} ${M + L},${M + H - n * rise + rise}`} fill="none" stroke="#18181b" strokeWidth={fs / 5} />
      <line x1={M} y1={M + H + rise} x2={M + L} y2={M + H + rise} stroke="#71717a" strokeWidth={fs / 10} />
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#dc2626" strokeWidth={fs / 3.5} />
      <circle cx={x1} cy={y1} r={fs / 3} fill="#dc2626" /><circle cx={x2} cy={y2} r={fs / 3} fill="#dc2626" />
      <text transform={`translate(${(x1 + x2) / 2} ${(y1 + y2) / 2 - fs * 0.7}) rotate(${ang})`} fontSize={fs} textAnchor="middle" fill="#dc2626" fontWeight="600" fontFamily="monospace">{`Länge ${p.length} mm`}</text>
    </svg>
  );
}

function GatePreview({ p, base }) {
  const { plan, layout } = gateAnsicht(p, base);
  return (
    <div className="sm:col-span-3" data-testid={`gate-preview-${p.id}`}>
      <Label>Vorschau (Ansicht)</Label>
      <AnsichtCanvas plan={plan} layout={layout} />
    </div>
  );
}

export function ProjectCard({ p, index, base, onChange, onRemove }) {
  const up = (patch) => onChange({ ...p, ...patch });
  const Icon = ICONS[p.type];
  const unit = UNIT_MM[base?.unit] || 1, U = base?.unit || "mm";
  return (
    <section className="bg-white border border-zinc-200 p-4 md:p-5 space-y-4" data-testid={`project-${index}`}>
      <div className="flex items-center gap-3 border-b border-zinc-200 pb-3">
        <span className="w-8 h-8 rounded-full bg-zinc-900 text-white flex items-center justify-center"><Icon size={15}/></span>
        <h3 className="text-sm font-semibold uppercase tracking-widest text-zinc-800 flex-1">{index + 1}. {PROJECT_TYPES[p.type].label}</h3>
        <div className="w-44 flex items-center gap-1.5"><span className="text-[10px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">{p.type === "gate" ? "Torhöhe" : "Höhe"} ({U})</span>
          <NumInput step={10 / unit} min={300 / unit} unit={unit} value={p.height} onChange={(v) => up({ height: v })} testId={`project-height-${index}`} />
        </div>
        <button type="button" onClick={onRemove} className="text-zinc-500 hover:text-red-600" title="Entfernen" data-testid={`project-remove-${index}`}><Trash2 size={16}/></button>
      </div>
      {p.type === "gate" ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><Label>Tortyp</Label>
            <select value={p.gateType} onChange={(e) => up({ gateType: e.target.value, width: GATE_DEFAULTS[e.target.value].width })} className={inp} data-testid={`gate-project-type-${index}`}>
              {Object.entries(GATE_DEFAULTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select></div>
          <div><Label>Lichte Breite ({U})</Label><NumInput step={50 / unit} unit={unit} value={p.width} onChange={(v) => up({ width: v })} testId={`gate-project-width-${index}`} /></div>
          <GateOptions g={{ ...p, type: p.gateType }} set={up} testId={`gate-project-${index}`} showPosts />
          <p className="sm:col-span-3 text-[11px] text-zinc-500">Lichte = freie Durchgangsbreite zwischen den Torpfosten. Torhöhe oben eintragen (bei Tor im Zaun wird sie automatisch aus Zaunhöhe + Sockel berechnet).</p>
          <GatePreview p={p} base={base} />
        </div>
      ) : p.type === "stairs" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2"><Label>Schräge Länge – von erster bis letzter Stufe ({U})</Label><NumInput step={50 / unit} unit={unit} value={p.length} onChange={(v) => up({ length: v })} testId={`stairs-length-${index}`} /></div>
            <div><Label>Anzahl Stufen</Label><NumInput step={1} value={p.steps} onChange={(v) => up({ steps: v })} testId={`stairs-steps-${index}`} /></div>
            <p className="col-span-2 text-[11px] text-zinc-500">Messen Sie entlang der Treppe von der Vorderkante der ersten bis zur Vorderkante der letzten Stufe. Für weitere Treppenläufe einfach eine zweite Treppe hinzufügen.</p>
          </div>
          <StairsPreview p={p} index={index} />
        </div>
      ) : (
        <>
          <div><Label>Form</Label><ShapeEditor p={p} up={up} base={base} withFree={p.type === "fence" || p.type === "balcony"} /></div>
          {p.type === "balcony" && (
            <div data-testid={`balcony-handrail-${index}`}><Label>Handlauf entlang des Geländers?</Label>
              <Choice options={[["ja", "Ja, mit Handlauf"], ["nein", "Nein, ohne Handlauf"]]} value={p.handrail ? "ja" : "nein"} onChange={(v) => up({ handrail: v === "ja" })} testId={`handrail-${p.id}`} cols="grid-cols-2" />
            </div>
          )}
          {p.type === "fence" && (
            <>
              <div><Label>Montage</Label>
                <Choice options={[["fundament", "Auf Fundament / Boden", "Alu-Pfosten einbetoniert oder auf Bodenplatte"], ["sockel", "Auf Sockel / Mauer", "Betonsockel mit oder ohne Mauerpfeiler"]]} value={p.montage} onChange={(montage) => up({ montage })} testId={`montage-${p.id}`} cols="grid-cols-1 sm:grid-cols-2" />
              </div>
              {p.form !== "free" && <div><Label>Tore</Label><GatesEditor p={p} up={up} base={base} /></div>}
              <SockelEditor p={p} up={up} base={base} />
            </>
          )}
        </>
      )}
    </section>
  );
}

export function AddProjectButtons({ onAdd }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="add-project">
      {Object.entries(PROJECT_TYPES).map(([k, v]) => { const I = ICONS[k]; return (
        <button type="button" key={k} onClick={() => onAdd(k)} data-testid={`add-project-${k}`}
                className="text-left border border-dashed border-zinc-400 bg-white px-3 py-3 hover:border-zinc-900 flex items-start gap-2">
          <I size={16} className="mt-0.5 shrink-0"/><div><div className="text-sm font-semibold text-zinc-900 flex items-center gap-1"><Plus size={12}/> {v.label}</div><div className="text-[11px] text-zinc-500 leading-tight">{v.hint}</div></div>
        </button>
      ); })}
    </div>
  );
}

export { newProject };
